import { createTheme } from '@mui/material/styles';

/**
 * Pro Portal design system.
 *
 * Token-driven MUI theme with light + dark color schemes (CSS variables).
 * Brand: deep slate primary, sharp red accent, Inter, soft shadows,
 * compact-but-breathable data density.
 */

// ── Core brand ramps ──
export const slate = {
  50: '#f6f8f9',
  100: '#eceff2',
  200: '#d9e0e4',
  300: '#b9c6cd',
  400: '#8fa3ad',
  500: '#64808c',
  600: '#4b626e',
  700: '#3c505b',
  800: '#2c3d46',
  900: '#1d2b33',
  950: '#131e24',
};

export const red = {
  main: '#E10404',
  light: '#ff4444',
  dark: '#b00303',
};

// ── Soft elevation (light) ──
const softShadow = {
  xs: '0 1px 2px rgba(16, 24, 40, 0.05)',
  sm: '0 1px 2px rgba(16, 24, 40, 0.05), 0 1px 3px rgba(16, 24, 40, 0.06)',
  md: '0 2px 4px rgba(16, 24, 40, 0.05), 0 4px 12px rgba(16, 24, 40, 0.08)',
  lg: '0 8px 16px rgba(16, 24, 40, 0.08), 0 12px 32px rgba(16, 24, 40, 0.10)',
};

// ── Status badge tokens (soft bg + strong text), per scheme ──
const lightBadges = {
  success:   { bg: '#dcf5e0', color: '#166534' },
  danger:    { bg: '#fde3e3', color: '#b91c1c' },
  warning:   { bg: '#fdf0d2', color: '#854d0e' },
  info:      { bg: '#dbeafe', color: '#1d4ed8' },
  primary:   { bg: slate[200], color: slate[800] },
  secondary: { bg: '#eef0f2', color: '#57636b' },
  purple:    { bg: '#ede9fe', color: '#6d28d9' },
  cyan:      { bg: '#cffafe', color: '#0e7490' },
  teal:      { bg: '#ccfbf1', color: '#0f766e' },
  azure:     { bg: '#e0f2fe', color: '#0369a1' },
};

const darkBadges = {
  success:   { bg: 'rgba(74, 222, 128, 0.14)', color: '#6ee7a0' },
  danger:    { bg: 'rgba(248, 113, 113, 0.14)', color: '#fca5a5' },
  warning:   { bg: 'rgba(250, 204, 21, 0.13)', color: '#fbd66f' },
  info:      { bg: 'rgba(96, 165, 250, 0.14)', color: '#93c5fd' },
  primary:   { bg: 'rgba(185, 198, 205, 0.14)', color: slate[200] },
  secondary: { bg: 'rgba(148, 163, 173, 0.13)', color: '#aeb9c0' },
  purple:    { bg: 'rgba(167, 139, 250, 0.14)', color: '#c4b5fd' },
  cyan:      { bg: 'rgba(34, 211, 238, 0.13)', color: '#67e8f9' },
  teal:      { bg: 'rgba(45, 212, 191, 0.13)', color: '#5eead4' },
  azure:     { bg: 'rgba(56, 189, 248, 0.13)', color: '#7dd3fc' },
};

export const createAppTheme = () => {
  return createTheme({
    cssVariables: {
      colorSchemeSelector: 'data-color-scheme',
    },

    colorSchemes: {
      light: {
        palette: {
          primary: {
            main: slate[700],
            light: slate[500],
            dark: slate[900],
            contrastText: '#ffffff',
          },
          secondary: { ...red, contrastText: '#ffffff' },
          error: { main: '#d92d20' },
          warning: { main: '#dc6803' },
          info: { main: '#1570ef' },
          success: { main: '#079455' },
          background: {
            default: '#f6f7f9',
            paper: '#ffffff',
          },
          text: {
            primary: slate[900],
            secondary: slate[600],
          },
          divider: 'rgba(29, 43, 51, 0.10)',
          badges: lightBadges,
          highlight: {
            field: '#fdf0d2',
          },
        },
      },
      dark: {
        palette: {
          primary: {
            main: '#9fb7c2',
            light: '#c3d3db',
            dark: '#7695a3',
            contrastText: slate[950],
          },
          secondary: { main: '#f65656', light: '#ff8a8a', dark: '#d32f2f', contrastText: '#ffffff' },
          error: { main: '#f97066' },
          warning: { main: '#fdb022' },
          info: { main: '#53b1fd' },
          success: { main: '#47cd89' },
          background: {
            default: '#10181c',
            paper: '#161f25',
          },
          text: {
            primary: '#e8edf0',
            secondary: 'rgba(232, 237, 240, 0.64)',
          },
          divider: 'rgba(232, 237, 240, 0.10)',
          badges: darkBadges,
          highlight: {
            field: 'rgba(250, 204, 21, 0.12)',
          },
        },
      },
    },

    typography: {
      fontFamily:
        '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif',
      h1: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.025em' },
      h2: { fontWeight: 700, fontSize: '1.625rem', letterSpacing: '-0.02em' },
      h3: { fontWeight: 650, fontSize: '1.375rem', letterSpacing: '-0.015em' },
      h4: { fontWeight: 650, fontSize: '1.175rem', letterSpacing: '-0.01em' },
      h5: { fontWeight: 650, fontSize: '1rem', letterSpacing: '-0.005em' },
      h6: { fontWeight: 600, fontSize: '0.875rem' },
      subtitle1: { fontWeight: 600, fontSize: '0.875rem' },
      subtitle2: { fontWeight: 600, fontSize: '0.8125rem' },
      body1: { fontSize: '0.875rem', lineHeight: 1.6 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.6 },
      caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    },

    shape: {
      borderRadius: 8,
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          body {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          #root {
            min-height: 100vh;
          }
          .tabular-nums {
            font-variant-numeric: tabular-nums;
          }
          .cursor-pointer {
            cursor: pointer;
            user-select: none;
          }
          @keyframes pageFadeIn {
            from { opacity: 0; transform: translateY(4px); }
            to { opacity: 1; transform: none; }
          }
          .page-fade-in {
            animation: pageFadeIn 0.25s ease-out;
          }
          @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after {
              animation-duration: 0.001ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.001ms !important;
              scroll-behavior: auto !important;
            }
          }
        `,
      },

      // ── Buttons ──
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            borderRadius: 8,
            padding: '5px 14px',
            transition: 'background-color .15s ease, border-color .15s ease, box-shadow .15s ease, transform .1s ease',
            '&:active': { transform: 'scale(0.985)' },
            '&.Mui-focusVisible': {
              boxShadow: `0 0 0 3px rgba(${theme.vars.palette.primary.mainChannel} / 0.3)`,
            },
          }),
          sizeSmall: { fontSize: '0.75rem', padding: '3px 10px' },
          contained: ({ theme }) => ({
            boxShadow: softShadow.xs,
            '&:hover': { boxShadow: softShadow.sm },
            ...theme.applyStyles('dark', { boxShadow: 'none' }),
          }),
          outlined: ({ theme }) => ({
            borderColor: theme.vars.palette.divider,
            '&:hover': {
              borderColor: `rgba(${theme.vars.palette.text.primaryChannel} / 0.3)`,
              backgroundColor: `rgba(${theme.vars.palette.text.primaryChannel} / 0.03)`,
            },
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-focusVisible': {
              boxShadow: `0 0 0 3px rgba(${theme.vars.palette.primary.mainChannel} / 0.3)`,
            },
          }),
        },
      },

      // ── Inputs ──
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '0.8125rem',
            borderRadius: 8,
            backgroundColor: theme.vars.palette.background.paper,
            transition: 'box-shadow 0.15s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.vars.palette.divider,
              transition: 'border-color 0.15s ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `rgba(${theme.vars.palette.text.primaryChannel} / 0.3)`,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px rgba(${theme.vars.palette.primary.mainChannel} / 0.14)`,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.vars.palette.primary.main,
                borderWidth: 1.5,
              },
            },
          }),
          input: { padding: '8.5px 12px' },
          inputSizeSmall: { padding: '6px 10px' },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.8125rem' },
          sizeSmall: { fontSize: '0.75rem' },
        },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { fontSize: '0.7rem', marginTop: 2 } },
      },
      MuiSelect: {
        styleOverrides: {
          select: { fontSize: '0.8125rem' },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { fontSize: '0.8125rem', minHeight: 34, borderRadius: 6, margin: '0 4px', paddingLeft: 8, paddingRight: 8 },
        },
      },

      // ── Cards ──
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: ({ theme }) => ({
            borderColor: theme.vars.palette.divider,
            borderRadius: 12,
            boxShadow: softShadow.sm,
            backgroundImage: 'none',
            ...theme.applyStyles('dark', {
              boxShadow: 'none',
            }),
          }),
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: '14px 16px', '&:last-child': { paddingBottom: 14 } },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: '12px 16px' },
          title: { fontSize: '0.875rem', fontWeight: 600 },
          subheader: { fontSize: '0.75rem' },
        },
      },

      // ── Tables ──
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '0.8125rem',
            padding: '8px 12px',
            borderColor: theme.vars.palette.divider,
          }),
          head: ({ theme }) => ({
            fontWeight: 600,
            fontSize: '0.7rem',
            color: theme.vars.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '6px 12px',
            backgroundColor: theme.vars.palette.background.default,
            whiteSpace: 'nowrap',
          }),
          sizeSmall: { padding: '5px 10px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.MuiTableRow-hover:hover': {
              backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.04)`,
            },
          }),
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: { fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
        },
      },

      // ── Chips / Badges ──
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 999, height: 22, fontWeight: 500 },
          sizeSmall: { fontSize: '0.7rem', height: 20 },
          label: { paddingLeft: 9, paddingRight: 9 },
          labelSmall: { paddingLeft: 8, paddingRight: 8 },
        },
      },

      // ── Tabs ──
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 38 },
          indicator: { height: 2.5, borderRadius: '2px 2px 0 0' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8125rem',
            minHeight: 38,
            padding: '6px 14px',
            color: theme.vars.palette.text.secondary,
            transition: 'color .15s ease, background-color .15s ease',
            '&:hover': { color: theme.vars.palette.text.primary },
            '&.Mui-selected': { fontWeight: 600 },
          }),
        },
      },

      // ── Dialogs ──
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 14,
            boxShadow: softShadow.lg,
            backgroundImage: 'none',
            ...theme.applyStyles('dark', {
              border: `1px solid ${theme.vars.palette.divider}`,
            }),
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontSize: '1rem', fontWeight: 650, padding: '14px 20px' },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '12px 20px' },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '10px 20px' },
        },
      },

      // ── AppBar ──
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            boxShadow: 'none',
            borderBottom: `1px solid ${theme.vars.palette.divider}`,
          }),
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: '52px !important' },
        },
      },

      // ── Drawer ──
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRight: `1px solid ${theme.vars.palette.divider}`,
            boxShadow: 'none',
            backgroundImage: 'none',
          }),
        },
      },

      // ── Lists (sidebar nav) ──
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 8,
            padding: '5px 10px',
            transition: 'background-color .15s ease, color .15s ease',
            '&.Mui-selected': {
              backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.09)`,
              '&:hover': {
                backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.13)`,
              },
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { minWidth: 32 },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: { fontSize: '0.8125rem' },
        },
      },

      // ── Menus / Selects ──
      MuiMenu: {
        defaultProps: { disableScrollLock: true },
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 10,
            boxShadow: softShadow.md,
            border: `1px solid ${theme.vars.palette.divider}`,
            backgroundImage: 'none',
          }),
        },
      },

      // ── Alerts ──
      MuiAlert: {
        styleOverrides: {
          root: { fontSize: '0.8125rem', borderRadius: 10, padding: '6px 14px' },
        },
      },

      // ── Tooltips ──
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            fontSize: '0.75rem',
            borderRadius: 8,
            backgroundColor: slate[900],
            ...theme.applyStyles('dark', {
              backgroundColor: slate[100],
              color: slate[900],
            }),
          }),
        },
      },

      // ── Paper ──
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: ({ theme }) => ({ borderColor: theme.vars.palette.divider }),
        },
      },

      // ── Dividers ──
      MuiDivider: {
        styleOverrides: {
          root: ({ theme }) => ({ borderColor: theme.vars.palette.divider }),
        },
      },

      // ── Accordion ──
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '10px !important',
            '&:before': { display: 'none' },
            boxShadow: 'none',
            border: `1px solid ${theme.vars.palette.divider}`,
            backgroundImage: 'none',
          }),
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: { minHeight: 42, '&.Mui-expanded': { minHeight: 42 } },
          content: { margin: '8px 0', '&.Mui-expanded': { margin: '8px 0' } },
        },
      },

      // ── Skeleton ──
      MuiSkeleton: {
        defaultProps: { animation: 'wave' },
        styleOverrides: {
          root: { borderRadius: 6 },
        },
      },

      // ── Toggle buttons ──
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            borderColor: theme.vars.palette.divider,
            '&.Mui-selected': {
              backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.1)`,
              color: theme.vars.palette.primary.main,
              '&:hover': {
                backgroundColor: `rgba(${theme.vars.palette.primary.mainChannel} / 0.16)`,
              },
            },
          }),
        },
      },
    },
  });
};
